import { HttpClient, APIResponseType } from './types';
import { AxiosAdapter } from './AxiosAdapter'; // or FetchAdapter
import { QueryClient } from '@tanstack/react-query';
import queryClient from './queryClient';
import { header, handleLogout } from 'utils/auth';

class ApiService {
  private httpClient: HttpClient;
  private queryClient: QueryClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.queryClient = queryClient;
  }

  // Helper method to generate query key
  private getQueryKey(method: string, url: string, params?: Record<string, any>): any[] {
    return [method, url, params ? { ...params } : {}];
  }

  // Generic method to make GET requests with caching
  async get<T>(url: string, params?: Record<string, any>): Promise<APIResponseType<T>> {
    // Check if this is a blob request and disable caching
    if (params?.responseType === 'blob') {
      return this.httpClient.get<T>(url, params);
    }

    const queryKey = this.getQueryKey('GET', url, params);

    // Return cached data if available, otherwise fetch fresh data
    return this.queryClient.fetchQuery({
      queryKey,
      queryFn: () => this.httpClient.get<T>(url, params),
    });
  }

  // Generic method to make POST requests (with optional caching)
  async post<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    console.log("ApiService post method called with URL:", url);
    console.log("POST data:", data);

    if (enableCache) {
      const queryKey = this.getQueryKey('POST', url, { data, ...config });
      return this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => {
          console.log("Executing POST request via queryFn");
          return this.httpClient.post<T>(url, data, config);
        },
      });
    }
    return this.httpClient.post<T>(url, data, config);
  }

  // Generic method to make PUT requests (with optional caching)
  async put<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    if (enableCache) {
      const queryKey = this.getQueryKey('PUT', url, { data, ...config });
      return this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => this.httpClient.put<T>(url, data, config),
      });
    }
    return this.httpClient.put<T>(url, data, config);
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: any): Promise<APIResponseType<T>> {
    // Typically we don't cache DELETE requests
    return this.httpClient.delete<T>(url, config);
  }

  // Method to manually invalidate cache for specific endpoints
  invalidateCache(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url, params);

    console.log("🔄 Invalidating Cache for:", queryKey);

    this.queryClient.removeQueries({ queryKey })
  }

  invalidateCacheByPrefix(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url);
    console.log("🔄 Invalidating Cache by prefix for:", queryKey);
    this.queryClient.removeQueries({
      queryKey,
      exact: false, // important for partial match
    });
  }

  /**
   * Stream text response from server using Server-Sent Events or chunked response
   * @param url - API endpoint (relative to base URL)
   * @param body - Request body to send
   * @param onChunk - Callback function called for each chunk of data received
   * @param options - Optional configuration (headers, etc.)
   * @returns Promise that resolves when streaming is complete
   */
  async streamText(
    url: string,
    body: any,
    onChunk: (text: string) => void,
    options?: {
      headers?: Record<string, string>;
      onError?: (error: Error) => void;
      onComplete?: () => void;
    }
  ): Promise<void> {
    const baseURL = process.env.REACT_APP_API_URL || '';
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    // Add auth header if available
    const authHeader = header();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      // Force logout on unauthorized response.
      if (response.status === 401) {
        handleLogout();
        throw new Error('Access Denied. jwt expired');
      }

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Check if response body exists
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          onChunk(chunk);
        }
      } finally {
        reader.releaseLock();
      }

      // Call completion callback if provided
      if (options?.onComplete) {
        options.onComplete();
      }
    } catch (error: any) {
      console.error('Error in streamText:', error);
      
      // Call error callback if provided
      if (options?.onError) {
        options.onError(error instanceof Error ? error : new Error(String(error)));
      } else {
        // Re-throw if no error handler provided
        throw error;
      }
    }
  }

  // Method to clear entire cache
  // clearCache() {
  //   this.queryClient.clear();
  // }
}

// Create an instance of ApiService with the desired HTTP client
const httpClient = new AxiosAdapter(); // or new FetchAdapter()
const apiService = new ApiService(httpClient);

// Add S3 content fetching service
export const fetchS3ContentThroughBackend = async (s3Url: string): Promise<{ content: string; contentType?: string; }> => {
  try {
    const response = await httpClient.post<{ content: string; contentType?: string; }>('api/fetch-s3-content', {
      url: s3Url,
    });

    if (response.body) {
      return response.body;
    } else {
      throw new Error(response.message || 'Failed to fetch S3 content');
    }
  } catch (error) {
    console.error('Error fetching S3 content through backend:', error);
    throw error;
  }
};

export default apiService;

// Export all API services
export { default as DealerContentSettings } from './dealerContentSettings/dealerContentSettingsApiService';
export { default as MiaChatV2 } from './miaChat/miaChatV2ApiService';
export { default as EmailTemplateApiService } from './emailTemplate/emailTemplateApiService';
export { default as ImportExportApiService } from './importExport/importExportApiService';